import { EditOutlined, LeftOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Card, Flex, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { MenuProps } from 'antd/lib'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useStocksServiceDeleteInternationalNameEndpoint,
  useStocksServiceGetInternationalNameListEndpoint,
  useStocksServiceGetInternationalNameListEndpointKey
} from 'src/api/queries'
import {
  ApiError,
  GetSubstanceListResponseItem,
  InternationalNameUsability
} from 'src/api/requests'
import {
  DeleteConfirm,
  message,
  SearchBar,
  Select,
  TableActionButtons
} from 'src/components'
import { useTablePagination } from 'src/hooks'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler, resetPagination } from 'src/utils'

import { useUsabilityOptions } from '../hooks'

import { InternationalNamesModal } from './InternationalNamesModal'

export const InternationalNamesCard = () => {
  const { t } = useLocales()
  const navigate = useNavigate()
  const pagination = useTablePagination()

  const [search, setSearch] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [currentId, setCurrentId] = useState<string>()
  const [ordering, setOrdering] = useState<string>(
    InternationalNameUsability.ALL
  )

  const queryClient = useQueryClient()
  const { options } = useUsabilityOptions()

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    ...(!!search && {
      searchTerm: search
    }),
    ...(!!ordering && {
      usability: ordering as InternationalNameUsability
    })
  }

  const { data, isLoading } =
    useStocksServiceGetInternationalNameListEndpoint(queryParams)

  const { mutate } = useStocksServiceDeleteInternationalNameEndpoint({
    onError: (e: ApiError) => formErrorHandler(e.body.errors),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [useStocksServiceGetInternationalNameListEndpointKey]
      })
      message.success(t('medicine_register.international_name.delete_success'))
    }
  })

  const onSearch = (val: string) => {
    setSearch(val)
    resetPagination(pagination)
  }

  const onSelect = (val: string) => {
    setOrdering(val)
    resetPagination(pagination)
  }

  const onToggle = () => setIsOpen((prev) => !prev)

  const onCloseModal = () => {
    onToggle()
    setCurrentId(undefined)
  }

  const getActionItems = useMemo(
    () =>
      (id: string): MenuProps['items'] => {
        return [
          {
            key: 'edit',
            label: t('common.edit'),
            icon: <EditOutlined />,
            onClick: () => {
              onToggle()
              setCurrentId(id)
            }
          },
          {
            key: 'divider',
            type: 'divider'
          },
          {
            key: 'delete',
            label: (
              <DeleteConfirm
                title={t('medicine_register.international_name.delete')}
                onConfirm={() => mutate({ id })}
              />
            ),
            danger: true
          }
        ]
      },
    [t]
  )

  const columns: ColumnsType<GetSubstanceListResponseItem> = [
    {
      key: 'name',
      title: t('medicine_register.international_name.title'),
      render: ({ name, dosage }) => `${name} ${dosage}`
    },
    {
      key: 'actions',
      width: 64,
      render: ({ id }) => (
        <TableActionButtons items={getActionItems(id)} justify="center" />
      )
    }
  ]

  return (
    <Card>
      <Flex vertical gap={16}>
        <Flex gap={16} wrap="wrap">
          <Button
            icon={<LeftOutlined />}
            onClick={() => navigate(paths.medicinesRegister.root)}
          >
            {t('common.back')}
          </Button>

          <SearchBar className="flex-1" onSearch={onSearch} />

          <Select
            defaultValue={InternationalNameUsability.ALL}
            options={options}
            onSelect={onSelect}
          />

          <Button type="primary" onClick={onToggle}>
            {t('common.add')}
          </Button>
        </Flex>

        <Table
          rowKey="id"
          bordered
          loading={isLoading}
          dataSource={data?.data}
          columns={columns}
          scroll={{ x: 'max-content' }}
          pagination={{ ...pagination, total: data?.totalCount }}
        />
      </Flex>
      {isOpen && (
        <InternationalNamesModal id={currentId} onCancel={onCloseModal} />
      )}
    </Card>
  )
}
